import React from "react";
import { Box, Button, Chip, Paper, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Download as DownloadIcon,
  Event as EventIcon,
  LocalFireDepartment as LocalFireDepartmentIcon,
  MedicalServices as MedicalServicesIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  UploadFile as UploadFileIcon,
  Verified as VerifiedIcon,
  Visibility as VisibilityIcon,
  WarningAmber as WarningAmberIcon,
} from "@mui/icons-material";
import { downloadCertificationDocument } from "../../api/apiClient";
import { parseLocalDate } from "../../utils/dateUtils";

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const date = parseLocalDate(dateStr);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.ceil((date - now) / 86400000);
}

function statusFor(cert) {
  if (!cert) return "missing";
  const days = daysUntil(cert.expires);
  if (days === null) return "active"; // on file and never expires
  if (days < 0) return "expired";
  if (days <= 30) return "expiring";
  return "active";
}

function dateLabel(date) {
  if (!date) return "—";
  return parseLocalDate(date).toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

function remainingLabel(cert) {
  const days = daysUntil(cert?.expires);
  if (days === null) return "Lifetime credential";
  if (days < 0) return `Expired ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} ago`;
  if (days === 0) return "Expires today";
  if (days <= 30) return `Expires in ${days} day${days === 1 ? "" : "s"}`;
  const months = Math.floor(days / 30);
  return `${months} month${months === 1 ? "" : "s"} remaining`;
}

function expiryPercent(issueDate, expiresDate) {
  if (!issueDate || !expiresDate) return 0;
  const issued = parseLocalDate(issueDate).getTime();
  const expires = parseLocalDate(expiresDate).getTime();
  if (Date.now() >= expires) return 100;
  if (Date.now() <= issued) return 0;
  return Math.round(((Date.now() - issued) / (expires - issued)) * 100);
}

function certificateIcon(name, primary) {
  const normalized = (name || "").toLowerCase();
  if (normalized.includes("fire")) return <LocalFireDepartmentIcon sx={{ fontSize: 22 }} />;
  if (/(cpr|aed|emt|ems|medic)/.test(normalized)) return <MedicalServicesIcon sx={{ fontSize: 22 }} />;
  return <VerifiedIcon sx={{ fontSize: 22, color: primary }} />;
}

/**
 * Mock-faithful credential card. Callbacks deliberately remain owned by the
 * role page so upload, detail, renewal, and document permissions stay intact.
 */
export default function CertificationCard({ certType, cert, onView, onUpload, onRenew, onDownload, onShare }) {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const ink = theme.palette.text.primary;
  const muted = theme.palette.text.secondary;
  const divider = alpha(ink, 0.12);
  const status = statusFor(cert);
  const isCompletion = cert?.source === "COURSE_COMPLETION";
  const name = isCompletion ? "Certificate of Completion" : (certType?.name || cert?.name || "Certification");
  const issuer = isCompletion && cert?.name
    ? cert.name.replace(/^Certificate of Completion\s*[—-]+\s*/i, "")
    : (certType?.issuer || cert?.issuer || "");
  const tones = {
    active: { label: "Active", color: theme.palette.secondary.main },
    expiring: { label: "Expiring Soon", color: theme.palette.warning.main },
    expired: { label: "Expired", color: theme.palette.error.main },
    missing: { label: "Not on File", color: muted },
  };
  const tone = tones[status];

  async function download() {
    if (onDownload) return onDownload(cert);
    if (!cert?.evidenceUrl) return undefined;
    if (cert.evidenceUrl.startsWith("/api/")) {
      const blob = await downloadCertificationDocument(cert.userId, cert.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = cert.documentName || `certificate-${cert.certNumber || cert.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return undefined;
    }
    window.open(cert.evidenceUrl, "_blank");
    return undefined;
  }

  if (status === "missing") {
    return (
      <Paper data-testid="certification-card" elevation={0} sx={{ p: 2.25, borderRadius: 1.5, border: `1px dashed ${divider}`, bgcolor: alpha(ink, 0.02), display: "flex", flexDirection: "column" }}>
        <CardHeader name={name} issuer={issuer} icon={certificateIcon(name, primary)} tone={tone} iconColor={muted} />
        <Typography sx={{ mt: "auto", mb: 1.25, color: theme.palette.warning.main, fontSize: 11, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase" }}>
          <WarningAmberIcon sx={{ fontSize: 14, verticalAlign: "text-bottom", mr: 0.75 }} />Required
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onUpload} variant="contained" size="small" startIcon={<UploadFileIcon />} sx={primaryButton(primary)} fullWidth>Upload now</Button>
          <Button onClick={onView} aria-label={`View ${name}`} variant="outlined" size="small" sx={outlineButton(divider, ink)}><VisibilityIcon fontSize="small" /></Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper data-testid="certification-card" elevation={0} sx={{ p: 2.25, borderRadius: 1.5, border: `1px solid ${divider}`, bgcolor: "background.paper", display: "flex", flexDirection: "column", minWidth: 0 }}>
      <CardHeader name={name} issuer={issuer} icon={certificateIcon(name, primary)} tone={isCompletion ? { label: "Course Completion", color: primary } : tone} iconColor={tone.color} />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, py: 1.5, borderTop: `1px solid ${divider}`, borderBottom: `1px solid ${divider}`, mb: 1.75 }}>
        <InfoRow label="Credential ID" value={cert?.certNumber || "—"} icon={<VerifiedIcon />} mono />
        <InfoRow label="Issued" value={dateLabel(cert?.issueDate)} icon={<EventIcon />} />
        <InfoRow label="Expires" value={cert?.expires ? dateLabel(cert.expires) : "Never"} icon={<ScheduleIcon />} />
      </Box>
      {cert?.expires && (
        <Box sx={{ mb: 1.75 }}>
          <Box data-testid="certification-expiry-progress" sx={{ height: 5, borderRadius: 99, overflow: "hidden", bgcolor: alpha(ink, 0.08), mb: 0.75 }}>
            <Box sx={{ height: "100%", width: `${expiryPercent(cert.issueDate, cert.expires)}%`, bgcolor: tone.color, borderRadius: 99 }} />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", color: muted, fontSize: 11.5 }}><span>Validity</span><b style={{ color: tone.color }}>{remainingLabel(cert)}</b></Box>
        </Box>
      )}
      <Box sx={{ display: "flex", gap: 1, mt: "auto", flexWrap: { xs: "wrap", sm: "nowrap" } }}>
        {status === "expired" || status === "expiring" ? <Button onClick={onRenew} variant="contained" size="small" startIcon={<RefreshIcon />} sx={primaryButton(tone.color)} fullWidth>Renew now</Button> : <Button onClick={download} disabled={!cert?.evidenceUrl} variant="contained" size="small" startIcon={<DownloadIcon />} sx={primaryButton(primary)} fullWidth>Download</Button>}
        <Button onClick={isCompletion ? onView : onShare || onView} disabled={!isCompletion && !onShare && !onView} variant="outlined" size="small" sx={outlineButton(divider, ink)} fullWidth>{isCompletion ? "View" : "Share"}</Button>
      </Box>
    </Paper>
  );
}

function CardHeader({ name, issuer, icon, tone, iconColor }) {
  return <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.875 }}><Box sx={{ width: 42, height: 42, borderRadius: 1, display: "grid", placeItems: "center", flexShrink: 0, bgcolor: alpha(iconColor, 0.12), color: iconColor }}>{icon}</Box><Box sx={{ minWidth: 0, flex: 1 }}><Typography sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.3, fontFamily: "var(--font-display)" }}>{name}</Typography><Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.375 }}>{issuer}</Typography></Box><Chip label={tone.label} size="small" sx={{ color: tone.color, bgcolor: alpha(tone.color, 0.12), border: `1px solid ${alpha(tone.color, 0.25)}`, fontSize: 10.5, fontWeight: 700, textTransform: "uppercase" }} /></Box>;
}

function InfoRow({ label, value, icon, mono }) {
  return <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, fontSize: 12.5 }}><Typography sx={{ color: "text.secondary", display: "inline-flex", alignItems: "center", gap: 0.75, fontSize: "inherit" }}>{React.cloneElement(icon, { sx: { fontSize: 14 } })}{label}</Typography><Typography sx={{ fontWeight: 600, fontSize: "inherit", fontFamily: mono ? "'JetBrains Mono', monospace" : "inherit" }}>{value}</Typography></Box>;
}

const primaryButton = (color) => ({ minHeight: 38, borderRadius: 1, textTransform: "none", fontWeight: 600, bgcolor: color, backgroundImage: "none", whiteSpace: "nowrap", "&:hover": { bgcolor: color, filter: "brightness(.94)" } });
const outlineButton = (divider, ink) => ({ minHeight: 38, borderRadius: 1, textTransform: "none", fontWeight: 600, borderColor: divider, color: ink, whiteSpace: "nowrap" });
